import { Grid, Paper, Typography } from "@mui/material";
import * as Yup from 'yup';
import { State } from '@progress/kendo-data-query';
import dayjs from 'dayjs';

import { ReactHookForm } from "../../components/hook-form/react-hook-form.component";
import { SiTrackPage } from "../../components/si-track-page";
import { WorkOrderReportPagingFilters } from "./work-order.filters";
import { SiTrackGrid } from "../../components/si-track-grid/si-track-grid";
import { UserPermissionName } from "../../models/user-permission-name";
import { workOderReportPagingColumns } from "./work-order-report-columns";
import { useReactHookForm } from "../../hooks/useReactHookForm";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { SelectResult } from "../../components/select/select-chain.component";
import { useSaveData } from "../../hooks/useSaveData";
import { workorderReportApiKeyStore } from "../../api/workorder-report-api-key-store";
import { useSnackbar } from "../../hooks/use-snackbar.hook";
import { GridColumnConfig, GridPreference } from "../../models/gridCoumnConfig";
import useDebounce from "../../hooks/use-debounce";
import { WorkOrderReport } from "../../models/work-order-report";
import { useLoadData } from "../../hooks/useLoadData";
import { ApiResponse } from "../../models/api-response";
import { gridApiKeyStore } from "../../api/grid-api-key-store";
import { DateFormat } from "../query-builder/hooks-form-querybuilder/form-date.field.component";

const styling = {
    paperStyle: { padding: '20px', borderRadius: '10px' },
};

interface CustomGridCellProps {
    field: string;
}
interface CellProps {
    workorder: any;
}

const workOrderReportSchema = Yup.object().shape({
    fromDate: Yup.string().required('From Date is required'),
});

const getFiltesFromUrl = (params: URLSearchParams) => {
    return {
        customerID: params.get('customerID'),
        officeID: params.get('officeID'),
        projectID: params.get('projectID'),
        unitID: params.get('unitID'),
    };
};

export const WorkOrderReportPaigngPage = () => {

    const isMultiSelectReportEnabled = process.env.REACT_APP_MULTI_SELECT_REPORT_FLAG === 'true';
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const [searchParams] = useSearchParams();

    const [defaultFilters, setDefaultFilter] = useState<any>();

    const [currSearch, setCurrSearch] = useState<any>(null);
    const [currFilters, setCurrFilters] = useState<any>(null);
    const [currGroups, setCurrGroups] = useState<any>(null);
    const [currPageNumber, setCurrPageNumber] = useState<any>(1);
    const [currSize, setCurrSize] = useState<any>(50);
    const [currSkip, setCurrSkip] = useState<any>(0);
    const [currSort, setCurrSort] = useState<any>([]);
    const [workOrderReportsPagingData, setWorkOrderReportsPagingData] = useState<any>();
    const [totalRecords, setTotalRecords] = useState<any>(0);

    const [selectedFilters, setSelectedFilters] = useState<SelectResult>(getFiltesFromUrl(searchParams));

    const form = useReactHookForm({
        defaultValues: {
            customer: { customerID: '', customerName: '' },
            office: { officeID: '', officeName: '' },
            project: { projectID: '', projectName: '' },
            unit: { unitID: '', unitName: '' },
            user: { userID: '', fullName: '' },
            service: { serviceID: '', serviceName: '' },
            dateCriteria: isMultiSelectReportEnabled ? [{ dateCriteriaValue: '', dateCriteria: '' }] : { dateCriteriaValue: '', dateCriteria: '' },
            workOrderStatus: isMultiSelectReportEnabled ? [{ workOrderStatusID: '', workOrderStatus: '' }] : { workOrderStatusID: '', workOrderStatus: '' },
            projectStatus: { statusValue: 'active', statusLabel: 'Active' },
            workOrderActiveStatus: { statusValue: 'active', statusLabel: 'Active' },
            action: '',
        },
    });

    const { isLoading, data: workOrderReportData, mutateAsync: mutateWorkOrderAsync } = useSaveData<any>(
        workorderReportApiKeyStore.getWorkOrderReportsPaging());

    const { isLoading: isExcelLoading, mutateAsync: mutateExcelAsync } = useSaveData<any>(
        workorderReportApiKeyStore.exportWorkOrderReportExcel());

    const { isLoading: isFilterExcelLoading, mutateAsync: mutateFilterExcelAsync } = useSaveData<any>(
        workorderReportApiKeyStore.exportWorkOrderReportExcel());

    useEffect(() => {
        if (workOrderReportData) {
            const result: any = workOrderReportData;
            setWorkOrderReportsPagingData(result?.data);
            setTotalRecords(result?.totalRecords);
        }
    }, [workOrderReportData])

    useEffect(() => {
        const filteringData = {
            pageNumber: 1,
            pageSize: defaultFilters?.take ? defaultFilters?.take : 50,
            sortExp: defaultFilters?.sort?.length > 0 ? defaultFilters?.sort[0]?.field : null,
            sortDir: defaultFilters?.sort?.length > 0 ? defaultFilters?.sort[0]?.dir?.toUpperCase() : null,
            searchText: null,
            filterJson: defaultFilters?.filter ? JSON.stringify(defaultFilters?.filter) : null,
            groupJson: defaultFilters?.group ? JSON.stringify(defaultFilters?.group) : null,
            customerID: 0,
            officeID: 0,
            projectID: 0,
            unitID: 0,
            serviceID: 0,
            inspectorID: 0,
            woStatusIDs: [],
            dateCriterias: ["created"],
            dateFrom: dayjs().subtract(2, 'week').format(DateFormat.SIMPLE_DATE),
            dateTo: dayjs().format(DateFormat.SIMPLE_DATE),
            projectStatus: 'active',
            workOrderActiveStatus: "active",
            departments: []
            // scheduledDateFrom: filtersData?.scheduledDateFrom ?? '',
            // scheduledDateTo: filtersData?.scheduledDateTo ?? '',
        };
        setSelectedFilters(filteringData);
    }, [])

    const isWithinSixMonths = (dateFrom: any, dateTo: any) => {
        const from = new Date(dateFrom);
        const to = new Date(dateTo);

        // Add 6 months to dateFrom
        const sixMonthsAfter = new Date(from);
        sixMonthsAfter.setMonth(sixMonthsAfter.getMonth() + 6);

        // Check if dateTo is <= dateFrom + 6 months
        return to <= sixMonthsAfter;
    };

    const handleSubmit = (filtersData: any) => {
        if (filtersData?.toDate == "") {
            showSnackbar(`Please select the To Date.`, 'error');
            return;
        }
        // if (filtersData?.fromDate !== "" && filtersData?.toDate !== "" && !isWithinSixMonths(filtersData?.fromDate, filtersData?.toDate)) {
        //     showSnackbar(`Please select a date range within 6 months.`, 'error');
        //     return;
        // }
        const filteringData = {
            pageNumber: 1,
            pageSize: defaultFilters?.take ? defaultFilters?.take : 50,
            sortExp: defaultFilters?.sort?.length > 0 ? defaultFilters?.sort[0]?.field : null,
            sortDir: defaultFilters?.sort?.length > 0 ? defaultFilters?.sort[0]?.dir?.toUpperCase() : null,
            searchText: null,
            filterJson: defaultFilters?.filter ? JSON.stringify(defaultFilters?.filter) : null,
            groupJson: defaultFilters?.group ? JSON.stringify(defaultFilters?.group) : null,
            customerID: filtersData?.customer?.customerID ? filtersData?.customer?.customerID : 0,
            officeID: filtersData?.office?.officeID ? filtersData?.office?.officeID : 0,
            projectID: filtersData?.project?.projectID ? filtersData?.project?.projectID : 0,
            unitID: filtersData?.unit?.unitID ? filtersData?.unit?.unitID : 0,
            serviceID: filtersData?.service?.serviceID ? filtersData?.service?.serviceID : 0,
            inspectorID: filtersData?.user?.userID ? filtersData?.user?.userID : 0,
            woStatusIDs: isMultiSelectReportEnabled ? filtersData?.workOrderStatusID?.map((s: any) => s?.workOrderStatusID) ?? [] :
                [filtersData?.workOrderStatus?.workOrderStatusID ? filtersData?.workOrderStatus?.workOrderStatusID : 0],
            dateCriterias: isMultiSelectReportEnabled ? filtersData?.dateCriteria?.map((s: any) => s?.dateCriteriaValue) ?? [] :
                [filtersData?.dateCriteria?.dateCriteriaValue ?? 0],
            dateFrom: filtersData?.fromDate ?? '',
            dateTo: filtersData?.toDate ?? '',
            projectStatus: filtersData?.projectStatus?.statusValue ?? '',
            workOrderActiveStatus: filtersData?.workOrderActiveStatus?.statusValue ?? '',
            departments: filtersData?.departments?.map((s: any) => s?.departmentID) ?? [],
            //isBasicFilter: true,
            //isGridExport: null
            // scheduledDateFrom: filtersData?.scheduledDateFrom ?? '',
            // scheduledDateTo: filtersData?.scheduledDateTo ?? '',
        };
        mutateWorkOrderAsync({ data: filteringData });
        setSelectedFilters(filteringData);

        setCurrSize(defaultFilters?.take ? defaultFilters?.take : 50);
        setCurrSkip(0);
        setCurrSort(defaultFilters?.sort?.length > 0 ? defaultFilters?.sort : []);
        setCurrFilters(defaultFilters?.filter ? defaultFilters?.filter : null);
        setCurrSearch(null);
    };

    const WorkOrderCell = ({ workorder }: CellProps) => {
        const [isHovered, setIsHovered] = useState(false);
        const handleClickWorkorderNumber = () => {
            navigate(`/workorders?searchType=workorders&searchText=${workorder?.workOrderNumber}`);
        };
        return (
            <td style={{ cursor: 'pointer' }} onClick={handleClickWorkorderNumber}>
                <Typography
                    component="span"
                    style={{
                        color: isHovered ? '#0000b5' : '#0000b5',
                        textDecoration: isHovered ? 'underline' : 'none',
                        cursor: 'pointer',
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {workorder?.workOrderNumber}
                </Typography>
            </td>
        );
    };

    const InvoiceAmountCell = ({ workorder }: CellProps) => {
        const invoiceAmount = workorder?.invoiceAmountNumeric;

        const formattedAmount =
            invoiceAmount !== undefined && invoiceAmount !== null
                ? `$${parseFloat(invoiceAmount)?.toFixed(2)}` // Add dollar sign
                : '$0.00'; // Fallback to "0.00" if invoiceAmount is not available
        return (
            <td>
                <Typography component="span">{formattedAmount}</Typography>
            </td>
        );
    };

    const PriceAmountCell = ({ workorder }: CellProps) => {
        const priceAmount = workorder?.priceNumeric;

        const formattedAmount =
            priceAmount !== undefined && priceAmount !== null
                ? `$${parseFloat(priceAmount)?.toFixed(2)}` // Add dollar sign
                : '$0.00'; // Fallback to "0.00" if invoiceAmount is not available
        return (
            <td>
                <Typography component="span">{formattedAmount}</Typography>
            </td>
        );
    };

    const AccuralAmountCell = ({ workorder }: CellProps) => {
        const accrualAmount = workorder?.accrualAmountNumeric;

        const formattedAmount =
            accrualAmount !== undefined && accrualAmount !== null
                ? `$${parseFloat(accrualAmount)?.toFixed(2)}` // Add dollar sign
                : '$0.00'; // Fallback to "0.00" if invoiceAmount is not available
        return (
            <td>
                <Typography component="span">{formattedAmount}</Typography>
            </td>
        );
    };

    const JobsiteCell = ({ workorder }: CellProps) => {
        const [isHovered, setIsHovered] = useState(false);
        const handleClickJobsite = () => {
            window.open(`/units/profile/${workorder?.unitID}`, "_blank");
        };
        return (
            <td style={{ cursor: 'pointer' }} onClick={handleClickJobsite}>
                <Typography
                    component="span"
                    style={{
                        color: isHovered ? '#0000b5' : '#0000b5',
                        textDecoration: isHovered ? 'underline' : 'none',
                        cursor: 'pointer',
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {workorder?.jobsite}
                </Typography>
            </td>
        );
    };

    // const WorkHoursCell = ({ workorder }: CellProps) => {
    //   const accrualAmount = workorder?.workedHoursNumeric;

    //   const formattedAmount =
    //     accrualAmount !== undefined && accrualAmount !== null
    //       ? `$${parseFloat(accrualAmount)?.toFixed(2)}` // Add dollar sign
    //       : '$0.00'; // Fallback to "0.00" if invoiceAmount is not available
    //   return (
    //     <td>
    //       <Typography component="span">{formattedAmount}</Typography>
    //     </td>
    //   );
    // };

    const CustomGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
        if (field === 'workOrderNumber') {
            // Return a function that takes a data item and returns a JSX element
            return (dataItem: any) => {
                return <WorkOrderCell workorder={dataItem.dataItem} />;
            };
        }
        if (field === 'invoiceAmountNumeric') {
            // Return a function that takes a data item and returns a JSX element
            return (dataItem: any) => {
                return <InvoiceAmountCell workorder={dataItem.dataItem} />;
            };
        }
        if (field === 'accrualAmountNumeric') {
            // Return a function that takes a data item and returns a JSX element
            return (dataItem: any) => {
                return <AccuralAmountCell workorder={dataItem.dataItem} />;
            };
        }
        if (field === 'priceNumeric') {
            // Return a function that takes a data item and returns a JSX element
            return (dataItem: any) => {
                return <PriceAmountCell workorder={dataItem.dataItem} />;
            };
        }
        if (field === 'jobsite') {
            // Return a function that takes a data item and returns a JSX element
            return (dataItem: any) => {
                return <JobsiteCell workorder={dataItem.dataItem} />;
            };
        }

        // if (field === 'workedHoursNumeric') {
        //   // Return a function that takes a data item and returns a JSX element
        //   return (dataItem: any) => {
        //     return <WorkHoursCell workorder={dataItem.dataItem} />;
        //   };
        // }

        return undefined;
    };

    //============Paginations Funtions Start======================================

    const handlePageChange = async (pageNumber: any, pageSize: any, pageSkip: any) => {

        const sorting: any = currSort?.length > 0 && currSort[0];
        setSelectedFilters((prev: any) => ({
            ...prev,
            pageNumber,
            pageSize,
        }));

        setCurrPageNumber(pageNumber);
        setCurrSize(pageSize);
        setCurrSkip(pageSkip);

        await mutateWorkOrderAsync({
            data: {
                ...selectedFilters,
                pageNumber,
                pageSize,
                sortExp: sorting?.field ?? null,
                sortDir: sorting?.dir?.toUpperCase() ?? null,
                filterJson: currFilters ? JSON.stringify(currFilters) : null,
                groupJson: currGroups ? JSON.stringify(currGroups) : null
                //isBasicFilter: null
            },
        });
    }

    const handleColumnSort = async (columnSort: any[]) => {
        const sorting: any = columnSort?.length > 0 && columnSort[0];
        setCurrSort(columnSort);
        await mutateWorkOrderAsync({
            data: {
                ...selectedFilters,
                pageNumber: currPageNumber,
                pageSize: currSize,
                sortExp: sorting?.field ?? null,
                sortDir: sorting?.dir?.toUpperCase() ?? null,
                filterJson: currFilters ? JSON.stringify(currFilters) : null,
                groupJson: currGroups ? JSON.stringify(currGroups) : null
                //isBasicFilter: null
            },
        });
    }

    const handleColumnFilter = async (columnFilter: any) => {
        const sorting: any = currSort?.length > 0 && currSort[0];
        setCurrFilters(columnFilter);
        setCurrPageNumber(1);
        setCurrSkip(0);
        await mutateWorkOrderAsync({
            data: {
                ...selectedFilters,
                pageNumber: 1,
                pageSize: currSize,
                sortExp: sorting?.field ?? null,
                sortDir: sorting?.dir?.toUpperCase() ?? null,
                filterJson: columnFilter ? JSON.stringify(columnFilter) : null,
                groupJson: currGroups ? JSON.stringify(currGroups) : null
                //isBasicFilter: null
            },
        });

    }

    const handleColumnGroup = async (columnGroup: any) => {
        const sorting: any = currSort?.length > 0 && currSort[0];
        setCurrGroups(columnGroup);
        setCurrPageNumber(1);
        setCurrSkip(0);
        await mutateWorkOrderAsync({
            data: {
                ...selectedFilters,
                pageNumber: 1,
                pageSize: currSize,
                sortExp: sorting?.field ?? null,
                sortDir: sorting?.dir?.toUpperCase() ?? null,
                filterJson: currFilters ? JSON.stringify(currFilters) : null,
                groupJson: columnGroup ? JSON.stringify(columnGroup) : null
                //isBasicFilter: null
            },
        });
    }

    const handleSearchData = async (searchText: any) => {
        const sorting: any = currSort?.length > 0 && currSort[0];
        setCurrSearch(searchText ?? null);
        if (searchText) {
            await mutateWorkOrderAsync({
                data: {
                    ...selectedFilters,
                    pageNumber: currPageNumber,
                    pageSize: currSize,
                    sortExp: sorting?.field ?? null,
                    sortDir: sorting?.dir?.toUpperCase() ?? null,
                    searchText: searchText ?? null,
                    filterJson: currFilters ? JSON.stringify(currFilters) : null,
                    groupJson: currGroups ? JSON.stringify(currGroups) : null
                    //isBasicFilter: null
                },
            });
        }
    }

    // const handleExportExcel = async () => {
    //     // Omit unwanted keys from selectedFilters
    //     await mutateExcelAsync({
    //         data: {
    //             ...selectedFilters, searchText: currSearch ?? null//, isGridExport: true
    //             // , isBasicFilter: null 
    //         }
    //     });
    //     showSnackbar(`Your export is in progress. You will receive an email once it is ready.`, 'success');
    // }

    const handleExportExcel = async () => {
    const sorting: any = currSort?.length > 0 && currSort[0];
    
    await mutateExcelAsync({
        data: {
            ...selectedFilters,
            // Current grid state params - same as what's passed when fetching grid data
            pageNumber: currPageNumber,
            pageSize: currSize,
            sortExp: sorting?.field ?? null,
            sortDir: sorting?.dir?.toUpperCase() ?? null,
            searchText: currSearch ?? null,
            filterJson: currFilters ? JSON.stringify(currFilters) : null,
            groupJson: currGroups ? JSON.stringify(currGroups) : null,
        }
    });
    showSnackbar(`Your export is in progress. You will receive an email once it is ready.`, 'success');
}



    const handleExportExcelFromFilter = async () => {
        // Omit unwanted keys from selectedFilters
        const { pageNumber, pageSize, sortExp, sortDir, searchText, filterJson, ...exportFilters } = selectedFilters || {};
        const excelPayload = {
            ...exportFilters//, isGridExport: null//, isBasicFilter: null
        };
        await mutateFilterExcelAsync({ data: excelPayload });
        showSnackbar(`Your export is in progress. You will receive an email once it is ready.`, 'success');
    }

    const handleResetPagination = async () => {
        setCurrSize(50);
        setCurrSkip(0);
        setCurrSort([]);
        setCurrFilters(null);
        setCurrSearch(null);
        await mutateWorkOrderAsync({
            data: {
                ...selectedFilters,
                pageNumber: 1,
                pageSize: 50,
                sortExp: null,
                sortDir: null,
                filterJson: null,
                groupJson: null
                //isBasicFilter: true
            },
        });
    }

    //============Paginations Funtions End======================================

    // -------------------------------------------------Grid Layout Start------------------------------

    const {
        isLoading: isLoadingGridLayout,
        data: gridLayouts,
        refetch: refetchGridLayout
    } = useLoadData<ApiResponse<GridPreference>>(
        gridApiKeyStore.getGridLayout('WorkOrderReport', 'WorkOrderReport')
    );

    const { isLoading: isResetLoading, mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
    const [gridConfig, setGridConfig] = useState<{
        dataState: State | undefined;
        columns: any[] | undefined;
    }>();

    const [filteredWorkOrderReports, setFilteredWorkOrderReports] = useState<WorkOrderReport[]>([]);

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    useEffect(() => {
        const filteredWorkOrderReportList = workOrderReportsPagingData?.filter((WorkOrderReport: any) =>
            Object.values(WorkOrderReport).some(
                (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            )
        );
        setFilteredWorkOrderReports(filteredWorkOrderReportList || []);
    }, [debouncedSearchTerm, workOrderReportsPagingData]);

    const handleInputChange = (value: string) => {
        setSearchTerm(value);
    };

    const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
        const data = {
            filterJSON: JSON.stringify(gridDataState),
            columnJSON: JSON.stringify(gridColumns),
            gridName: 'WorkOrderReport',
            pageName: 'WorkOrderReport',
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });
    };

    const handleResetLayout = () => {
        const data = {
            filterJSON: null,
            columnJSON: null,
            gridName: 'WorkOrderReport',
            pageName: 'WorkOrderReport',
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });

        handleResetPagination();
    };

    useEffect(() => {
        if (!gridLayouts?.data) return;

        try {
            const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

            const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

            setDefaultFilter(filterData);

            // if (filterData && columnJson) {
            setGridConfig({
                columns: columnJson,
                dataState: filterData,
            });
            // }
        } catch (error) {
            console.log('Error parsing grid layout JSON:', error);
        }
    }, [gridLayouts?.data]);

    // -------------------------------------------------Grid Layout End------------------------------

    const handleShowAll = () => {
        form.handleSubmit(handleSubmit)();
    };

    const handleFilterChange = (filterType: any, filterValue: any) => {
        setSelectedFilters(prev => {
            const updated = { ...prev };

            switch (filterType) {
                case "customer":
                    updated.customerID = filterValue?.customerID ? filterValue?.customerID : 0;
                    break;
                case "office":
                    updated.officeID = filterValue?.officeID ? filterValue?.officeID : 0;
                    break;
                case "project":
                    updated.projectID = filterValue?.projectID ? filterValue?.projectID : 0;
                    break;
                case "unit":
                    updated.unitID = filterValue?.unitID ? filterValue?.unitID : 0;
                    break;
                case "service":
                    updated.serviceID = filterValue?.serviceID ? filterValue?.serviceID : 0;
                    break;
                case "inspector":
                    updated.inspectorID = filterValue?.userID ? filterValue?.userID : 0;
                    break;
                case "fromDate":
                    updated.dateFrom = filterValue ?? '';
                    break;
                case "toDate":
                    updated.dateTo = filterValue ?? '';
                    break;
                case "status":
                    updated.woStatusIDs = isMultiSelectReportEnabled ? filterValue?.map((s: any) => s?.workOrderStatusID) ?? [] :
                        [filterValue?.workOrderStatus?.workOrderStatusID ? filterValue?.workOrderStatusID : 0];
                    break;
                case "departments":
                    updated.departments = filterValue?.map((s: any) => s?.departmentID) ?? [];
                    break;
                case "projectStatus":
                    updated.projectStatus = filterValue?.statusValue ?? '';
                    break;
                case "workOrderActiveStatus":
                    updated.workOrderActiveStatus = filterValue?.statusValue ?? '';
                    break;
                case "dateCriterias":
                    updated.dateCriterias = isMultiSelectReportEnabled ? filterValue?.map((s: any) => s?.dateCriteriaValue) ?? [] :
                        [filterValue?.dateCriteriaValue ?? 0];
                    break;
                default:
                    console.warn("Unknown filterType:", filterType);
            }

            return updated;
        });
    }

    return (
        <SiTrackPage title="Work Order Reports">
            <ReactHookForm formName="WORK_ORDER_REPORT_FORM" onSubmit={() => { }} formContextProps={form}>
                <Grid container spacing={1}>
                    <Grid item lg={12} sm={12} xl={12} xs={12}>
                        <WorkOrderReportPagingFilters
                            isReportLoading={isLoading || isLoadingGridLayout || isResetLoading}
                            isExportExcelLoading={isFilterExcelLoading}
                            onShowAllWorkOrder={handleShowAll} onExportExcel={handleExportExcelFromFilter}
                            onFilterChange={handleFilterChange} />
                    </Grid>
                    <Grid item lg={12} sm={12} xl={12} xs={12}>
                        <Paper elevation={0} style={styling.paperStyle}>
                            <SiTrackGrid
                                fileTitle='work_order_report'
                                columns={workOderReportPagingColumns}
                                dataItemKey="workOrderID"

                                currentPageSize={currSize}
                                currentSkip={currSkip}
                                currentSort={currSort}
                                currentFilter={currFilters}
                                currentGroup={currGroups}
                                isServerSearching={true}
                                isExportServerExcel={true}
                                totalRecords={totalRecords}

                                onPageChange={handlePageChange}
                                onSortChange={handleColumnSort}
                                onFilterChange={handleColumnFilter}
                                onGroupChange={handleColumnGroup}
                                onSearchChange={handleSearchData}
                                onExportExcel={handleExportExcel}

                                // -----------------Grid Layout-------
                                dataItems={filteredWorkOrderReports || []}
                                reorderable
                                handleSearch={handleInputChange}
                                handleSaveLayout={handleSaveLayout}
                                isSaveLayout={true}
                                gridConfigFromApi={gridConfig}
                                handleResetLayout={handleResetLayout}
                                // -----------------Grid Layout-------


                                actionPermissions={{
                                    export: UserPermissionName.DOWNLOAD_GRID,
                                }}
                                customCell={(field) =>
                                    CustomGridCell({
                                        field,
                                    })
                                }
                                isBusy={isLoading || isLoadingGridLayout || isResetLoading}
                            />
                        </Paper>
                    </Grid>
                </Grid>
            </ReactHookForm>
        </SiTrackPage>
    );
}